import { Box, Center, HStack, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrevious } from "react-use";

import { Card } from "adminComponents/atoms/Card";
import { Modal } from "adminComponents/atoms/Modal";
import { Text } from "adminComponents/atoms/Text";
import { CapsuleMachineAnimation } from "adminComponents/molecules/CapsuleMachineAnimation";
import { pxToRem } from "adminComponents/utils";
import { useErrorToast } from "adminComponents/utils/toast";
import { useExchangeTicketForRandomItem } from "links/lib/features/season";
import { ExchangeTicketForRandomItemResponse } from "links/lib/features/season/useExchangeTicketForRandomItem";
import { AvatarItemRarityLevels, IAvatarItem } from "links/lib/types";
import { ConfirmButton } from "sessionComponents/molecules/ConfirmButton";
import { UnlockedItem } from "sharedComponents/atoms/UnlockedItem";
import { ReactComponent as TicketIconSVG } from "sharedComponents/organisms/Season1MapHeader/resource/ticket_icon.svg";

import { ReactComponent as CapsuleSVG } from "./resource/capsule_solo.svg";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  ticketCount: number;
  randomItemsAvailable: boolean;
}

export const CapsuleMachineModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  ticketCount,
  randomItemsAvailable,
}: IProps) => {
  const { t } = useTranslation("admin", {
    useSuspense: false,
  });

  const [showCapsuleMachineAniimation, setShowCapsuleMachineAnimation] =
    useState(false);
  const [showUnlockedItemAnimation, setShowUnlockedItemAnimation] =
    useState(false);
  const [unlockedItem, setUnlockedItem] = useState<IAvatarItem | undefined>(
    undefined
  );

  const handleExchangeTicketSuccess = (
    data: ExchangeTicketForRandomItemResponse
  ) => {
    const { avatar_item } = data;
    if (avatar_item) {
      setUnlockedItem(avatar_item);
    }
  };

  const exchangeTicket = useExchangeTicketForRandomItem({
    onSuccess: handleExchangeTicketSuccess,
  });
  useErrorToast(exchangeTicket.error);
  const previousExchangeTicketForRandomItemIsLoading = usePrevious(
    exchangeTicket.isLoading
  );

  useEffect(() => {
    if (
      previousExchangeTicketForRandomItemIsLoading &&
      !exchangeTicket.isLoading &&
      unlockedItem &&
      !exchangeTicket.error
    ) {
      setShowCapsuleMachineAnimation(true);
    }
  }, [
    exchangeTicket.isLoading,
    previousExchangeTicketForRandomItemIsLoading,
    unlockedItem,
    exchangeTicket.error,
  ]);

  const handleCapsuleMachineAnimationEnd = () => {
    setShowCapsuleMachineAnimation(false);
    setShowUnlockedItemAnimation(true);
  };

  const handleUnlockedItemAnimationEnd = () => {
    setShowUnlockedItemAnimation(false);
    setUnlockedItem(undefined);
  };

  return (
    <Modal
      title={t("common.capsuleMachine")}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      showBackButton={false}
    >
      <Box w="full">
        <VStack
          p={pxToRem(16)}
          spacing={pxToRem(32)}
          w="full"
          h="full"
          alignItems="center"
          justifyContent="center"
        >
          <Text variant="adminP1">{t("capsuleMachineModal.tradeTicket")}</Text>
          <Box h={pxToRem(250)} w={pxToRem(250)}>
            <CapsuleSVG aria-label={t("capsuleMachineModal.capsuleAlt")} />
          </Box>

          <HStack spacing={pxToRem(16)}>
            <TicketIconSVG />
            <Text>
              {t("capsuleMachineModal.ticketCount", { count: ticketCount })}
            </Text>
          </HStack>
          {!randomItemsAvailable && (
            <Card
              borderColor="primary.tan"
              backgroundColor="primary.tan"
              variant="adminCardSolid"
              marginBottom={pxToRem(40)}
            >
              <Center>
                <Text variant="adminP2">
                  {t("capsuleMachineModal.unlockedAll")}
                </Text>
              </Center>
            </Card>
          )}
          <ConfirmButton
            p={pxToRem(24)}
            confirmedWhenChanged={[exchangeTicket.isLoading]}
            onClick={() => exchangeTicket.mutate()}
            variant="buttonFilled"
            disabled={ticketCount < 1 || !randomItemsAvailable}
            isLoading={exchangeTicket.isLoading}
          >
            <Text variant="adminP1">{t("capsuleMachineModal.exchange")}</Text>
          </ConfirmButton>
        </VStack>
      </Box>
      {unlockedItem && showUnlockedItemAnimation && (
        <Box
          position="absolute"
          top="2"
          bottom="2"
          left="2"
          right="2"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgColor="primary.white"
          borderRadius="xl"
        >
          <UnlockedItem
            item={unlockedItem}
            show={showUnlockedItemAnimation}
            handleComplete={handleUnlockedItemAnimationEnd}
          />
        </Box>
      )}
      <CapsuleMachineAnimation
        show={!!unlockedItem && showCapsuleMachineAniimation}
        handleEnd={handleCapsuleMachineAnimationEnd}
        rarity={unlockedItem?.rarity || AvatarItemRarityLevels.Level1}
      />
    </Modal>
  );
};
